import React, { useState, useCallback } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';

import { SVGIcon } from '@superops/super-icons';
import { Button, Modal, TextContainer } from '@superops/ui-design-system';

import { getTranslatedText } from '@nile/components';

interface AlertBulkActionProps {
  records: any;
  actionClick: Function;
  disableAction: Boolean;
}

function AlertBulkAction({
  records,
  actionClick,
  disableAction = false
}: AlertBulkActionProps) {
  const intl = useIntl();
  const [deleteActive, setDeleteActive] = useState(false);
  const [resolveActive, setResolveActive] = useState(false);
  const handleDeleteModalChange = useCallback(
    () => setDeleteActive(!deleteActive),
    [deleteActive]
  );
  const handleAlertModalChange = useCallback(
    () => setResolveActive(!resolveActive),
    [resolveActive]
  );

  const activator = (
    <Button
      onClick={handleDeleteModalChange}
      icon={<SVGIcon name="Delete" />}
      disabled={disableAction}
    >
      <FormattedMessage id="common.delete" />
    </Button>
  );

  const resolveActivator = (
    <Button
      onClick={handleAlertModalChange}
      icon={<SVGIcon name="Check" />}
      disabled={disableAction}
    >
      <FormattedMessage id="asset.alertViews.resolvebtn" />
    </Button>
  );

  // when records in RMM-home we use totalRecords and when records is in asset, we use length property.
  const totalRecords = records?.length || records?.totalRecords;

  return (
    <div className="fx">
      {totalRecords > 0 && (
        <Button outline selected>
          {totalRecords}{' '}
          <FormattedMessage id="rmm.patchManagement.bulkActions.selected" />
        </Button>
      )}
      <div className="ml-10">
        <Modal
          activator={resolveActivator}
          open={resolveActive}
          onClose={handleAlertModalChange}
          variant="confirmation"
          title={getTranslatedText(intl, 'common.warning')}
          primaryAction={{
            content: getTranslatedText(intl, 'asset.alertViews.resolvebtn'),
            onAction: () => {
              handleAlertModalChange();
              actionClick('RESOLVE');
            }
          }}
          secondaryActions={[
            {
              content: getTranslatedText(intl, 'common.cancel'),
              onAction: handleAlertModalChange
            }
          ]}
        >
          <TextContainer>
            <FormattedMessage
              id="rmm.alert.resolve.confirmation"
              values={{ totalAlerts: totalRecords }}
            />
          </TextContainer>
        </Modal>
      </div>
      <div className="ml-10">
        <Modal
          activator={activator}
          open={deleteActive}
          onClose={handleDeleteModalChange}
          variant="confirmation"
          title={getTranslatedText(intl, 'common.warning')}
          primaryAction={{
            destructive: true,
            content: getTranslatedText(intl, 'common.delete'),
            onAction: () => {
              handleDeleteModalChange();
              actionClick('MOVE_TRASH');
            }
          }}
          secondaryActions={[
            {
              content: getTranslatedText(intl, 'common.cancel'),
              onAction: handleDeleteModalChange
            }
          ]}
        >
          <TextContainer>
            <FormattedMessage
              id="rmm.alert.delete.confirmation"
              values={{ totalAlerts: totalRecords }}
            />
          </TextContainer>
        </Modal>
      </div>
    </div>
  );
}

export default injectIntl(AlertBulkAction);
